import { useEffect, useMemo, useState } from "react";
import { fmtDate } from "@/lib/dates";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { GraduationCap, Plus, Trash2, UserPlus, Minus, Check, Pencil, CalendarDays } from "lucide-react";

const EMPTY = { name: "", description: "", total_sessions: 6, date: "" };
const showDate = (d) => (d ? fmtDate(d) : "Date TBC");

export default function Trainings() {
  const [courses, setCourses] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [enrollId, setEnrollId] = useState("");

  const load = () => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ name: c.name, description: c.description || "", total_sessions: c.total_sessions || 1, date: c.date || "" });
    setEditId(c.id); setOpen(true);
  };

  const saveCourse = async () => {
    if (!form.name) { toast.error("Training name is required"); return; }
    const payload = { ...form, total_sessions: parseInt(form.total_sessions) || 1 };
    try {
      if (editId) await api.put(`/courses/${editId}`, payload);
      else await api.post("/courses", payload);
      toast.success(editId ? "Training updated" : "Training created");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch { toast.error("Could not save training"); }
  };

  const removeCourse = async (id) => {
    try { await api.delete(`/courses/${id}`); toast.success("Course removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const enroll = async () => {
    if (!enrollId) { toast.error("Select a member"); return; }
    try {
      await api.post(`/courses/${enrollFor.id}/enroll`, { member_id: enrollId });
      toast.success("Member enrolled");
      setEnrollFor(null); setEnrollId(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enroll"); }
  };

  const setProgress = async (enr, delta, max) => {
    const next = Math.min(max, Math.max(0, enr.completed_sessions + delta));
    try { await api.put(`/enrollments/${enr.id}`, { completed_sessions: next }); load(); }
    catch { toast.error("Could not update"); }
  };

  const unenroll = async (id) => {
    try { await api.delete(`/enrollments/${id}`); load(); }
    catch { toast.error("Could not remove"); }
  };

  const availableForEnroll = useMemo(() => {
    if (!enrollFor) return [];
    const enrolled = new Set((enrollFor.enrollments || []).map((e) => e.member_id));
    return members.filter((m) => !enrolled.has(m.id));
  }, [enrollFor, members]);

  return (
    <div className="space-y-6" data-testid="trainings-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#526050]">{courses.length} trainings</p>
        <Button data-testid="add-course-button" onClick={openNew}
          className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
          <Plus className="h-4 w-4 mr-2" /> New Training
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {courses.map((c) => {
          const total = c.total_sessions || 1;
          const completed = (c.enrollments || []).filter((e) => e.completed_sessions >= total).length;
          return (
            <Card key={c.id} className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid={`course-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#0284C7] flex items-center justify-center text-white">
                    <GraduationCap className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-[#1C241B]">{c.name}</h3>
                    <p className="text-xs text-[#7A8A78]">{total} sessions · {(c.enrollments || []).length} enrolled · {completed} completed</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-[#0369A1]" data-testid={`course-date-${c.id}`}>
                      <CalendarDays className="h-3.5 w-3.5" />{showDate(c.date)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`} className="h-8 w-8 text-[#526050]">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEnrollFor(c); setEnrollId(""); }} data-testid={`enroll-course-${c.id}`} className="h-8 w-8 text-[#2D5A27]">
                    <UserPlus className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => removeCourse(c.id)} data-testid={`delete-course-${c.id}`} className="h-8 w-8 text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="mt-3 text-sm text-[#526050]">{c.description}</p>

              <div className="mt-5 space-y-4">
                {(c.enrollments || []).length === 0 && <p className="text-sm text-[#7A8A78]">No members enrolled yet.</p>}
                {(c.enrollments || []).map((e) => {
                  const pct = Math.round((e.completed_sessions / total) * 100);
                  const done = e.completed_sessions >= total;
                  return (
                    <div key={e.id} data-testid={`enrollment-${e.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-[#1C241B] flex items-center gap-1.5">
                          {done && <Check className="h-4 w-4 text-[#2D5A27]" />}{e.member_name}
                          {e.self_enrolled && <span className="text-[10px] uppercase tracking-wider text-[#0369A1] bg-[#E0F2FE] rounded px-1.5 py-0.5">self-enrolled</span>}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#7A8A78]">{e.completed_sessions}/{total}</span>
                          <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => setProgress(e, -1, total)} data-testid={`progress-minus-${e.id}`}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => setProgress(e, 1, total)} data-testid={`progress-plus-${e.id}`}>
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => unenroll(e.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* New course dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="course-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit Training" : "New Training"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Training name</Label>
              <Input data-testid="course-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" data-testid="course-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Total sessions</Label>
                <Input type="number" min={1} data-testid="course-sessions-input" value={form.total_sessions}
                  onChange={(e) => setForm({ ...form, total_sessions: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea data-testid="course-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={saveCourse} data-testid="save-course-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">{editId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll dialog */}
      <Dialog open={!!enrollFor} onOpenChange={(v) => !v && setEnrollFor(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="enroll-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Enroll Member</DialogTitle></DialogHeader>
          <div className="py-2 space-y-1.5">
            <Label>Member — {enrollFor?.name}</Label>
            <Select value={enrollId} onValueChange={setEnrollId}>
              <SelectTrigger data-testid="enroll-member-select"><SelectValue placeholder="Select a member" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {availableForEnroll.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollFor(null)}>Cancel</Button>
            <Button onClick={enroll} data-testid="confirm-enroll-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">Enroll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
